/** sessionStorage key of the Smart Audit deep link to reopen after login (spec D13, §10.4). */
export const RETURN_TO_KEY = 'sa.returnTo';

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * Only same-origin module paths: `/smart-audit` followed by the end, `/`, `?` or `#`. Anything that a browser could
 * read as another origin (`//host`, `\`), that carries control characters or that has a `..` path segment, plain or
 * percent-encoded (which could climb out of the module) is refused, so the value can never become an open redirect even if someone writes to
 * sessionStorage directly. A scheme needs no check of its own: the leading `/smart-audit` already excludes it.
 */
const isValidReturnTo = (path: unknown): path is string => {
  if (typeof path !== 'string') return false;
  if (!/^\/smart-audit(?:$|[/?#])/.test(path)) return false;
  if (path.includes('//') || path.includes('\\') || CONTROL_CHARS.test(path)) return false;
  const pathname = path.split(/[?#]/, 1)[0];
  // A percent-encoded dot (`%2e`, any case) could be decoded into a `..` segment later, so it is refused outright.
  if (/%2e/i.test(pathname)) return false;
  return !pathname.split('/').includes('..');
};

/**
 * Remembers `path` (pathname + search + hash) when it is a Smart Audit page; any other path clears an earlier value so
 * a login that started elsewhere goes home as before.
 */
export const saveReturnTo = (path: string): void => {
  try {
    if (isValidReturnTo(path)) sessionStorage.setItem(RETURN_TO_KEY, path);
    else sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Storage disabled: the user simply lands on the home page after login.
  }
};

/** Reads and always deletes the stored path; returns it only when it is still a valid Smart Audit path. */
export const consumeReturnTo = (): string | null => {
  try {
    const path = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return isValidReturnTo(path) ? path : null;
  } catch {
    return null;
  }
};
